import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  Banknote,
  CheckCircle2,
  Eye,
  Inbox,
  Loader2,
  Mail,
  MousePointerClick,
  Phone,
  RefreshCw,
  Save,
  ShoppingBag,
  Sparkles,
  Train,
  Users,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TrafficTab, AffiliatesTab } from "../components/AdminAnalytics";
import LogsTab from "../components/AdminLogs";

const ORDER_STATUSES = [
  { id: "pending_payment", label: "Pending payment", tone: "amber" },
  { id: "confirmed_paid", label: "Confirmed (paid)", tone: "green" },
  { id: "trains_booked", label: "Trains booked", tone: "blue" },
  { id: "hostels_locked", label: "Hostels locked", tone: "blue" },
  { id: "itinerary_sent", label: "Itinerary sent", tone: "green" },
  { id: "cancelled", label: "Cancelled", tone: "red" },
];

const INQUIRY_STATUSES = [
  { id: "new", label: "New", tone: "amber" },
  { id: "quoted", label: "Quoted", tone: "blue" },
  { id: "won", label: "Won", tone: "green" },
  { id: "lost", label: "Lost", tone: "red" },
];

const toneClass = {
  amber: "bg-gold/15 text-gold border-gold/40",
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  blue: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  red: "bg-accent-red/15 text-accent-red border-accent-red/40",
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
};

function StatusPill({ value, options }) {
  const opt = options.find((o) => o.id === value) || { label: value || "—", tone: "amber" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-display tracking-widest uppercase ${toneClass[opt.tone] || toneClass.amber}`}>
      {opt.label}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="iq-card p-5 flex items-start gap-4">
      <div className="h-10 w-10 inline-flex items-center justify-center bg-gold/10 border border-gold/30 rounded-sm text-gold shrink-0">
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="iq-overline text-cream-dim/70 mb-1">{label}</div>
        <div className="font-display text-cream text-3xl tracking-wider leading-none">{value}</div>
        {sub && <div className="text-cream-dim text-xs font-serif italic mt-1">{sub}</div>}
      </div>
    </div>
  );
}

/* ─── Order row with inline editor ─── */
function OrderRow({ order, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    status: order.status,
    notes: order.admin_notes || "",
    assigned_conductor: order.assigned_conductor || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const dirty =
    draft.status !== order.status ||
    (draft.notes || "") !== (order.admin_notes || "") ||
    (draft.assigned_conductor || "") !== (order.assigned_conductor || "");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await onSave(order.id, draft);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`admin-order-${order.id.slice(0, 8)}`} className="iq-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-panel-border/20 transition-colors text-left"
      >
        <div className="col-span-2 font-display text-gold tracking-[0.2em] text-xs">
          #{order.id.slice(0, 8).toUpperCase()}
        </div>
        <div className="col-span-3 min-w-0">
          <div className="font-display text-cream tracking-wider text-sm truncate">{order.contact_name}</div>
          <div className="text-cream-dim text-[11px] truncate">{order.contact_email}</div>
        </div>
        <div className="col-span-2 text-cream-dim text-xs">{order.travel_window}</div>
        <div className="col-span-1 text-cream-dim text-xs">{order.travellers} pax</div>
        <div className="col-span-2 font-display text-gold text-base">€{Number(order.total_eur || 0).toLocaleString()}</div>
        <div className="col-span-2 flex items-center justify-end gap-2">
          <StatusPill value={order.status} options={ORDER_STATUSES} />
        </div>
      </button>

      {open && (
        <div className="border-t border-panel-border bg-ink/40 p-5 grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div>
              <div className="iq-overline mb-1">Items</div>
              <ul className="text-sm text-cream space-y-1">
                {(order.items || []).map((it) => (
                  <li key={it.id} className="flex justify-between gap-3">
                    <span className="truncate">{it.name} <span className="text-cream-dim/60 text-[11px]">× {it.qty}</span></span>
                    <span className="text-gold shrink-0">€{(it.price_eur * it.qty).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-sm text-cream-dim space-y-1">
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold/70" /> {order.contact_email}</div>
              {order.contact_phone && (
                <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold/70" /> {order.contact_phone}</div>
              )}
              <div>Created: {fmtDate(order.created_at)}</div>
              {order.notes && <div className="italic">"{order.notes}"</div>}
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="iq-label">Status</label>
              <select
                data-testid={`admin-order-status-${order.id.slice(0, 8)}`}
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="iq-input"
              >
                {ORDER_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="iq-label">Assigned conductor</label>
              <input
                data-testid={`admin-order-conductor-${order.id.slice(0, 8)}`}
                value={draft.assigned_conductor}
                onChange={(e) => setDraft({ ...draft, assigned_conductor: e.target.value })}
                className="iq-input"
                placeholder="Name of staff member"
              />
            </div>
            <div>
              <label className="iq-label">Internal notes</label>
              <textarea
                data-testid={`admin-order-notes-${order.id.slice(0, 8)}`}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className="iq-input min-h-[80px]"
                placeholder="Operator confirmations, special requests, follow-ups…"
              />
            </div>
            {err && <div className="text-accent-red text-sm">{err}</div>}
            <button
              type="button"
              data-testid={`admin-order-save-${order.id.slice(0, 8)}`}
              onClick={save}
              disabled={!dirty || saving}
              className="iq-btn-primary w-full justify-center disabled:opacity-50"
            >
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Save className="h-4 w-4" /> Save changes</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InquiryRow({ inquiry, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    status: inquiry.status || "new",
    notes: inquiry.admin_notes || "",
    assigned_conductor: inquiry.assigned_conductor || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const dirty =
    draft.status !== (inquiry.status || "new") ||
    (draft.notes || "") !== (inquiry.admin_notes || "") ||
    (draft.assigned_conductor || "") !== (inquiry.assigned_conductor || "");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await onSave(inquiry.id, draft);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`admin-inquiry-${inquiry.id.slice(0, 8)}`} className="iq-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-panel-border/20 transition-colors text-left"
      >
        <div className="col-span-3 min-w-0">
          <div className="font-display text-cream tracking-wider text-sm truncate">{inquiry.organization}</div>
          <div className="text-cream-dim text-[11px] truncate">{inquiry.contact_name}</div>
        </div>
        <div className="col-span-3 text-cream-dim text-xs truncate">{inquiry.contact_email}</div>
        <div className="col-span-2 text-cream-dim text-xs">{inquiry.travel_window}</div>
        <div className="col-span-2 text-cream-dim text-xs">{inquiry.group_size} pax · {inquiry.age_range || "—"}</div>
        <div className="col-span-2 flex items-center justify-end gap-2">
          <StatusPill value={inquiry.status || "new"} options={INQUIRY_STATUSES} />
        </div>
      </button>

      {open && (
        <div className="border-t border-panel-border bg-ink/40 p-5 grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div className="text-sm text-cream-dim space-y-1">
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold/70" /> {inquiry.contact_email}</div>
              {inquiry.contact_phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold/70" /> {inquiry.contact_phone}</div>}
              <div>Created: {fmtDate(inquiry.created_at)}</div>
            </div>
            {inquiry.special_requirements && (
              <div>
                <div className="iq-overline mb-1">Special requirements</div>
                <p className="text-sm text-cream font-serif italic leading-relaxed">{inquiry.special_requirements}</p>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <label className="iq-label">Status</label>
              <select
                data-testid={`admin-inquiry-status-${inquiry.id.slice(0, 8)}`}
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="iq-input"
              >
                {INQUIRY_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="iq-label">Assigned conductor</label>
              <input
                data-testid={`admin-inquiry-conductor-${inquiry.id.slice(0, 8)}`}
                value={draft.assigned_conductor}
                onChange={(e) => setDraft({ ...draft, assigned_conductor: e.target.value })}
                className="iq-input"
                placeholder="Name of staff member"
              />
            </div>
            <div>
              <label className="iq-label">Internal notes</label>
              <textarea
                data-testid={`admin-inquiry-notes-${inquiry.id.slice(0, 8)}`}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className="iq-input min-h-[80px]"
                placeholder="Quote details, follow-up plan…"
              />
            </div>
            {err && <div className="text-accent-red text-sm">{err}</div>}
            <div className="flex gap-2">
              <a
                href={`mailto:${inquiry.contact_email}?subject=InterQuest%20group%20enquiry%20%23${inquiry.id.slice(0, 8).toUpperCase()}`}
                className="iq-btn-ghost text-sm flex-1 justify-center"
              >
                <Mail className="h-4 w-4" /> Reply by email
              </a>
              <button
                type="button"
                data-testid={`admin-inquiry-save-${inquiry.id.slice(0, 8)}`}
                onClick={save}
                disabled={!dirty || saving}
                className="iq-btn-primary text-sm flex-1 justify-center disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin({ defaultTab = "orders" }) {
  const { user, checking } = useAuth();
  const isAdmin = user && user.email === "admin@interquest.app";

  const [tab, setTab] = useState(defaultTab);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderCounts, setOrderCounts] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [inquiryCounts, setInquiryCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [s, o, q] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/orders?limit=200"),
        api.get("/admin/group-inquiries?limit=200"),
      ]);
      setSummary(s.data);
      setOrders(o.data.orders || []);
      setOrderCounts(o.data.counts || {});
      setInquiries(q.data.inquiries || []);
      setInquiryCounts(q.data.counts || {});
    } catch (e) {
      console.warn("admin load failed", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
    else setLoading(false);
  }, [isAdmin]);

  const filteredOrders = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  const filteredInquiries = useMemo(() => {
    if (filter === "all") return inquiries;
    return inquiries.filter((i) => (i.status || "new") === filter);
  }, [inquiries, filter]);

  // Reset filter when switching tabs
  useEffect(() => {
    setFilter("all");
  }, [tab]);

  const saveOrder = async (id, patch) => {
    const { data } = await api.patch(`/admin/orders/${id}`, patch);
    if (data?.order) {
      setOrders((prev) => prev.map((o) => (o.id === id ? data.order : o)));
      loadAll(); // refresh counts
    }
  };

  const saveInquiry = async (id, patch) => {
    const { data } = await api.patch(`/admin/group-inquiries/${id}`, patch);
    if (data?.inquiry) {
      setInquiries((prev) => prev.map((i) => (i.id === id ? data.inquiry : i)));
      loadAll();
    }
  };

  // Wait for AuthContext's initial /auth/me probe to finish before deciding
  // whether to redirect. Without this guard, the page flashes "redirect to
  // /login" on every load before the cookie-based session is recognised.
  if (checking) {
    return (
      <div className="pt-32 pb-16 text-center">
        <Loader2 className="h-8 w-8 text-gold mx-auto animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div data-testid="admin-page" className="pt-safe-navbar-lg pb-20 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 md:px-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/" className="iq-overline text-cream-dim/70 inline-flex items-center gap-1 mb-1 hover:text-gold transition-colors">
              <ArrowLeft className="h-3 w-3" /> Back to site
            </Link>
            <h1 className="font-display text-cream text-4xl md:text-5xl tracking-wider">Conductor desk</h1>
          </div>
          <button
            type="button"
            data-testid="admin-refresh"
            onClick={loadAll}
            disabled={refreshing}
            className="iq-btn-ghost"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
            <SummaryCard
              testid="admin-tile-revenue"
              icon={Banknote}
              label="Revenue (paid)"
              value={`€${Number(summary.revenue_total_eur || 0).toLocaleString()}`}
              sub={`across ${summary.revenue_orders} orders`}
            />
            <SummaryCard
              testid="admin-tile-paid"
              icon={CheckCircle2}
              label="Paid orders"
              value={summary.orders_paid}
              sub={`${summary.paid_today} today`}
            />
            <SummaryCard
              testid="admin-tile-pending"
              icon={ShoppingBag}
              label="Pending payment"
              value={summary.orders_pending}
              sub="awaiting checkout"
            />
            <SummaryCard
              testid="admin-tile-inquiries"
              icon={Inbox}
              label="New inquiries"
              value={summary.inquiries_new}
              sub="schools + groups"
            />
            <SummaryCard
              testid="admin-tile-views"
              icon={Eye}
              label="Views (30d)"
              value={Number(summary.views_30d || 0).toLocaleString()}
              sub="page traffic"
            />
            <SummaryCard
              testid="admin-tile-clicks"
              icon={MousePointerClick}
              label="Affiliate clicks (30d)"
              value={Number(summary.affiliate_clicks_30d || 0).toLocaleString()}
              sub={`${Number(summary.users_total || 0)} users registered`}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-5 border-b border-panel-border overflow-x-auto">
          <button
            type="button"
            data-testid="admin-tab-orders"
            onClick={() => setTab("orders")}
            className={`px-4 py-3 font-display tracking-[0.15em] text-sm uppercase transition-colors border-b-2 whitespace-nowrap ${tab === "orders" ? "border-gold text-gold" : "border-transparent text-cream-dim hover:text-cream"}`}
          >
            <ShoppingBag className="inline h-4 w-4 mr-2" />
            Orders <span className="text-cream-dim/60">({orders.length})</span>
          </button>
          <button
            type="button"
            data-testid="admin-tab-inquiries"
            onClick={() => setTab("inquiries")}
            className={`px-4 py-3 font-display tracking-[0.15em] text-sm uppercase transition-colors border-b-2 whitespace-nowrap ${tab === "inquiries" ? "border-gold text-gold" : "border-transparent text-cream-dim hover:text-cream"}`}
          >
            <Users className="inline h-4 w-4 mr-2" />
            Group inquiries <span className="text-cream-dim/60">({inquiries.length})</span>
          </button>
          <button
            type="button"
            data-testid="admin-tab-traffic"
            onClick={() => setTab("traffic")}
            className={`px-4 py-3 font-display tracking-[0.15em] text-sm uppercase transition-colors border-b-2 whitespace-nowrap ${tab === "traffic" ? "border-gold text-gold" : "border-transparent text-cream-dim hover:text-cream"}`}
          >
            <Eye className="inline h-4 w-4 mr-2" />
            Traffic
          </button>
          <button
            type="button"
            data-testid="admin-tab-affiliates"
            onClick={() => setTab("affiliates")}
            className={`px-4 py-3 font-display tracking-[0.15em] text-sm uppercase transition-colors border-b-2 whitespace-nowrap ${tab === "affiliates" ? "border-gold text-gold" : "border-transparent text-cream-dim hover:text-cream"}`}
          >
            <MousePointerClick className="inline h-4 w-4 mr-2" />
            Affiliates
          </button>
          <button
            type="button"
            data-testid="admin-tab-logs"
            onClick={() => setTab("logs")}
            className={`px-4 py-3 font-display tracking-[0.15em] text-sm uppercase transition-colors border-b-2 whitespace-nowrap ${tab === "logs" ? "border-gold text-gold" : "border-transparent text-cream-dim hover:text-cream"}`}
          >
            <Activity className="inline h-4 w-4 mr-2" />
            Logs
          </button>
          <div className="flex-1" />
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              setRefreshing(true);
              try {
                await api.post("/admin/refresh-safety");
              } catch {}
              setRefreshing(false);
            }}
            data-testid="admin-refresh-safety"
            className="iq-btn-ghost text-xs whitespace-nowrap"
          >
            <Sparkles className="h-3 w-3" /> Refresh safety scores
          </a>
        </div>

        {/* Filter chips (orders + inquiries tabs only) */}
        {(tab === "orders" || tab === "inquiries") && (
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              onClick={() => setFilter("all")}
              data-testid="admin-filter-all"
              className={`px-3 py-1.5 rounded-sm text-xs font-display tracking-widest uppercase border transition-colors ${filter === "all" ? "bg-gold text-ink border-gold" : "text-cream-dim border-panel-border hover:border-gold/40"}`}
            >
              All ({tab === "orders" ? orders.length : inquiries.length})
            </button>
            {(tab === "orders" ? ORDER_STATUSES : INQUIRY_STATUSES).map((s) => {
              const n = (tab === "orders" ? orderCounts : inquiryCounts)[s.id] || 0;
              if (n === 0) return null;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setFilter(s.id)}
                  data-testid={`admin-filter-${s.id}`}
                  className={`px-3 py-1.5 rounded-sm text-xs font-display tracking-widest uppercase border transition-colors ${filter === s.id ? "bg-gold text-ink border-gold" : "text-cream-dim border-panel-border hover:border-gold/40"}`}
                >
                  {s.label} ({n})
                </button>
              );
            })}
          </div>
        )}

        {/* Lists */}
        {tab === "traffic" ? (
          <TrafficTab />
        ) : tab === "affiliates" ? (
          <AffiliatesTab />
        ) : tab === "logs" ? (
          <LogsTab />
        ) : loading ? (
          <div className="iq-card p-12 text-center">
            <Loader2 className="h-8 w-8 text-gold mx-auto animate-spin" />
          </div>
        ) : tab === "orders" ? (
          <div className="space-y-2" data-testid="admin-orders-list">
            {filteredOrders.length === 0 ? (
              <div className="iq-card p-12 text-center text-cream-dim font-serif italic">
                <Train className="h-8 w-8 text-gold/40 mx-auto mb-3" />
                No orders match this filter yet.
              </div>
            ) : (
              filteredOrders.map((o) => <OrderRow key={o.id} order={o} onSave={saveOrder} />)
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-inquiries-list">
            {filteredInquiries.length === 0 ? (
              <div className="iq-card p-12 text-center text-cream-dim font-serif italic">
                <Inbox className="h-8 w-8 text-gold/40 mx-auto mb-3" />
                No inquiries match this filter yet.
              </div>
            ) : (
              filteredInquiries.map((i) => <InquiryRow key={i.id} inquiry={i} onSave={saveInquiry} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}
